/* eslint-disable padding-line-between-statements */
import { BreakpointTrackerContext } from '@jutro/layout';
import React, { useCallback, useContext, useState, useMemo } from 'react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { WizardPage } from '@xengage/gw-portals-wizard-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { Loader, useModal, InlineNotification } from '@jutro/components';
import { LoadSaveService } from 'twia-capability-quoteandbind-residential';
import { useTranslator } from '@jutro/locale';
import config from 'app-config';
import _, { update } from 'lodash';
import wizardStyles from 'twia-portals-wizard-components-ui/common/TWIAWizard.module.scss'
import ErrorPopupUtil from 'ent-components-platform-react/util/ErrorPopupUtil.js';
import metadata from './AdditionalInformationPage.metadata.json5';
import styles from './AdditionalInformationPage.module.scss'
import TWIAAdditionalInsuredComponent from '../../components/TWIAAdditionalInformation/TWIAAdditionalInsuredComponent/TWIAAdditionalInsuredComponent.jsx';
import PrintDocumentUtil from '../../../twia-capability-quoteandbind-TWIAResidential-react/util/PrintDocumentUtil';
import PremiumFinanceComponent from '../../components/TWIAAdditionalInformation/PremiumFinanceComponent/PremiumFinanceComponent.jsx';
import DocumentUploadComponent from '../../components/TWIAAdditionalInformation/DocumentUpload/DocumentUploadComponent.jsx';


function AdditionalInformationPage(props) {
    const { id, wizardData: submissionVM, updateWizardData } = props;
    const breakpoint = useContext(BreakpointTrackerContext);
    const { isComponentValid, onValidate } = useValidation(id);
    const [showErrors, setShowErrors] = useState(false);
    const { authHeader } = useAuthentication();
    const translator = useTranslator();
    const [isLoading, setIsLoading] = useState(false);
    const { showAlert, showConfirm } = useModal();
    const [invalidComponent, setInvalidComponents] = useState([]);
    const [validationMessages, setValidationMessages] = useState([]);

    const findInvalidComponent = useCallback((isValid, componentID) => {
        setShowErrors(false);

        if (isValid === false) {
            setInvalidComponents((oldValue) => [...oldValue, componentID]);
        }

        if (isValid === true) {
            setInvalidComponents((oldValue) => oldValue.filter((val) => val !== componentID));
        }
    }, []);
    
    // US26285
    const quotePrintDocument = useCallback(async () => {
        setIsLoading(true);
        await PrintDocumentUtil.printDocument(submissionVM, showConfirm, showAlert, translator, authHeader);
        setIsLoading(false);

    }, [authHeader, showAlert, showConfirm, submissionVM, translator])

    const validationContent = useCallback(
        item => {
            return (
                <InlineNotification id={item} 
                    message={item}
                    type= "error"
                    className= {wizardStyles.marginBottom}
                    isDismissable= {false}
                />
            );
        },
        []
    );

    const generateOverrides = useMemo(() => {
        const overrides = validationMessages.map((item, index) => {
            return {
                [`errorMsg${index}`]: {
                    content: validationContent(item),
                },
            };
        });
        return Object.assign({}, ...overrides);
    }, [validationMessages, validationContent]);
    
    const resetElectronicPaymentData = useCallback(() => {
        const epayData = _.get(submissionVM, 'bindData.paymentDetails.electronicPaymentData_TWIA');

        if (epayData && epayData.aspects && !epayData.aspects.subtreeValid) {
            // eslint-disable-next-line no-secrets/no-secrets
            _.set(submissionVM, 'bindData.paymentDetails.electronicPaymentData_TWIA.value', null);
            updateWizardData(submissionVM);
        }
    }, [submissionVM, updateWizardData]);

    const onNext = useCallback(async () => {
        if (invalidComponent.length > 0) {
            setShowErrors(true);
            return false
        }
        setShowErrors(false);
        try {
            submissionVM.validationParams_TWIA.value = {
                validationLevel: 'default',
                validationWizardStep: 'AdditionalInfo',
                skipAllValidation: false
            };
            
            resetElectronicPaymentData();
            submissionVM.value = await LoadSaveService.updateQuotedSubmissionWithoutLOB_TWIA(
                    submissionVM.value,
                    authHeader
                )
            
            const validationIssues = _.get(submissionVM, 'errorsAndWarnings.value.validationIssues.issues');
            if (validationIssues && validationIssues.length > 0) {
                setShowErrors(true);
                setValidationMessages(_.uniq(validationIssues.map(vi => vi.reason)));
                return false;
            } else {
                return submissionVM;
            }
        } catch (error) {
            setShowErrors(false);
            ErrorPopupUtil.showSaveErrorPopup(showAlert, true)
            return false;
        }
    }, [invalidComponent, submissionVM, resetElectronicPaymentData, authHeader, showAlert])

    const overrideProps = {
        '@field': {
            // apply to all fields
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        additionalNamedInsuredsDetails: {
            submissionVM, updateWizardData, showErrors, findInvalidComponent, onValidate
        },
        additionalNamedInsuredsAccordion: {
            errorState: _.includes(invalidComponent, 'TWIAAdditionalInsuredDetails') && showErrors
        },
        premiumFinanceDetails: {
            submissionVM, updateWizardData, showErrors, findInvalidComponent, onValidate
        },
        premiumFinanceAccordion: {
            errorState: _.includes(invalidComponent, 'PremiumFinanceDetails') && showErrors
        },
        // 2025-05-22 - Debra Frum - DE19212 - TWIA Underwriting Response activities are triggering under open transactions
        //   Functionality for TWIA didn't get implemented when TFPA & TWIA portals merged during move to Guidewire Cloud
        // 2022-05-24 - Debra Frum - US22167 - PC / PE Underwriting Response Activity
        //   Don't create a Review activity when a document is uploaded within a TWIA transaction workflow
        uploadDocumentsDetails: {
            submissionVM, skipReviewActivity: true, updateWizardData, showErrors, findInvalidComponent, onValidate
        },
        validationMessages: {
            data: validationMessages
        },
        ...generateOverrides
    }
    const resolvers = {
        resolveCallbackMap: {
            quotePrintDocument
        },
        resolveClassNameMap: styles,
        resolveComponentMap: {
            AdditionalInsuredsComponent: TWIAAdditionalInsuredComponent,
            addlPremiumFinanceComponent: PremiumFinanceComponent,
            documentUploadComponent: DocumentUploadComponent,
        },
    }

    return (
        <WizardPage
            onNext={onNext}
        >
             <Loader loaded={!isLoading}>
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
                showErrors={showErrors}
            />
            </Loader>
        </WizardPage>
    )
}

export default AdditionalInformationPage
